<template>
  <v-container fluid ma-0 pa-0 px-4 >
    <div class="fixed-header" :class="{'mobile': $vuetify.breakpoint.smAndDown}"><!-- if has summary fields add class 'with-summary' -->
        <v-layout  align-center fill-height class="fixed-header-wrapper">
          <v-flex shrink class="text-truncate" pr-4>
            <v-tooltip color="secondary" bottom>
              <template v-slot:activator="{ on }">
                <h2 v-on="on" class="p-relative text-truncate" >
                  Search results for <span class="font-weight-bold">{{searchTerm}}</span>
                </h2>
              </template>
                <span>Import</span>
              </v-tooltip>
            <h4 class="pt-1 sub-header" :class="{ 'search-error-heading': searchError }">{{ searchError ? "Search failed" : ("All " + caseTypeName + " templates - " + total + " results") }}</h4>
          </v-flex>
          <v-spacer></v-spacer>
          <v-flex shrink v-if="canDelete">
            <v-btn flat color="error" @click="toggleSelectMode()">
              <v-icon class="mr-2">delete</v-icon>
              {{ selectMode ? 'Hide selection' : 'Select cases to delete' }}
            </v-btn>
          </v-flex>
        </v-layout>
      </div>
    <loading :isLoading="isSearching">
      <v-container mt-4 pa-0 fluid class="elevation-2 rounded-3" style="background-color:white">
        <div v-if="searchError" class="search-error" role="alert">{{ searchError }}</div>
        <v-tabs
          v-if="!searchError"
          v-model="currentTab"
          show-arrows
          color="white"
          slider-color="primary"
          class="search-results-tabs">
          <v-tab class="search-result-tab" ripple>
            {{ caseTypeName }}
            <span class="search-count-badge secondary white--text">{{ caseTotal }}</span>
          </v-tab>
          <v-tab v-if="showDocumentsTab" class="search-result-tab" ripple>
            Documents
            <span class="search-count-badge secondary white--text">{{ documentTotal }}</span>
          </v-tab>
          <v-tab-item :transition="false" :reverse-transition="false">
            <v-divider></v-divider>
            <div class="search-result-pane">
            <v-layout v-if="selectMode" row wrap align-center class="px-4 py-3">
              <v-flex grow>
                <h3 class="ma-0">{{ selectionCount }} selected</h3>
                <p class="ma-0">Delete the ticked cases, or every case matching this search.</p>
              </v-flex>
              <v-btn class="mr-2" color="error" :disabled="selectionCount === 0" @click="openDeleteDialog(false)">Delete selected</v-btn>
              <v-btn color="error" outline :disabled="!caseTotal" @click="openDeleteDialog(true)">Delete all {{ caseTotal }} matching</v-btn>
            </v-layout>
            <bucket-table
              :id="'resultsTable'"
              :ref="'resultsTable'"
              :dataProvider="dataProvider"
              :searchData="result"
              :resultTableMode="true"
              v-on:reload-search="reloadSearch"
              v-on:selections-update="setSelections"
              :taskToggled="selectMode"
              :showStatistics="false"
              :currentFilter="{}">
            </bucket-table>
            </div>
          </v-tab-item>
          <v-tab-item v-if="showDocumentsTab" :transition="false" :reverse-transition="false">
            <v-divider></v-divider>
            <v-container fluid class="pa-3">
              <h3 class="mb-2">
                Documents
                <span v-if="showingDocumentCount < documentTotal" class="sub-header body-1">
                  (showing {{ showingDocumentCount }} of {{ documentTotal }})
                </span>
              </h3>
              <document-search-results :documents="documentHits"></document-search-results>
            </v-container>
          </v-tab-item>
        </v-tabs>
      </v-container>
    </loading>
    <v-dialog v-model="deleteDialog" max-width="520">
      <v-card class="pa-4">
        <v-card-title class="pa-0 pb-2">
          <h2>Delete {{ deleteTargetCount }} cases?</h2>
        </v-card-title>
        <v-card-text class="pa-0">
          <p>Each case is deleted on its own. Permissions and delete scripts still apply. This cannot be undone.</p>
        </v-card-text>
        <v-card-actions class="ma-0 pa-0 pt-1">
          <v-btn color="error" :loading="deleting" @click="confirmDelete()">Yes, delete {{ deleteTargetCount }} cases</v-btn>
          <v-btn color="secondary" @click="deleteDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex"
import Loading from "@/components/shared/Loading.vue"
import BucketTable from "@/components/buckets/BucketTable.vue"
import DataProvider from "@/components/buckets/bucket-providers/SearchDataProvider.js"
import DocumentSearchResults from "@/components/search/DocumentSearchResults.vue"
import Helpers from "@/helpers.js"
import { logFeature } from "@/helpers/featureActivity"
import Vue from 'vue'

export default {
  components: { Loading, BucketTable, DocumentSearchResults },
  props: {
    dataProvider: { type: Object, default: function() { return DataProvider } },
    helpers: { type: Object, default: function() { return Helpers } }
  },
  data: () => ({
    prevPage: 0,
    currentTab: 0,
    pagination: {
      descending: false,
      page: 1,
      rowsPerPage: 5,
      sortBy: "",
      maxPagesShown: 15
    },
    result: {},
    caseType: null,
    selections: [],
    selectMode: false,
    deleteDialog: false,
    deleteAllMatching: false,
    deleting: false,
    documentSearch: { documents: [], total: 0 },
    caseTypeName: 'type',
    isSearching: false,
    searchError: ""
  }),
  computed: {
    ...mapState({
      searchTerm: state => state.cbroute.params.search_term,
      caseTypeId: state => state.cbroute.params.case_type_id,
      pageSize: state => state.currentUser.me.preferences.pageSize,
      currentUser: state => state.currentUser,
      permissions: state => (state.ui && state.ui.grantedPermissions) || []
    }),
    selectionCount() {
      return this.selections ? this.selections.length : 0
    },
    deleteTargetCount() {
      return this.deleteAllMatching ? this.caseTotal : this.selectionCount
    },
    canDelete() {
      if (this.currentUser && this.currentUser.isAccountAdmin) return true
      const code = this.caseType && this.caseType.code
      if (!code || !this.helpers || typeof this.helpers.hasPermission !== "function") return false
      return this.helpers.hasPermission(this.permissions, "Delete", code)
    },
    caseTotal() {
      if (this.result.total) { return this.result.total }
      return 0
    },
    total() {
      return this.caseTotal + this.documentTotal
    },
    documentHits() {
      if (this.documentSearch && this.documentSearch.documents) {
        return this.documentSearch.documents
      }
      return []
    },
    documentTotal() {
      if (this.documentSearch && this.documentSearch.total) {
        return this.documentSearch.total
      }
      return 0
    },
    showingDocumentCount() {
      return this.documentHits.length
    },
    showDocumentsTab() {
      return this.documentTotal > 0 || this.documentHits.length > 0
    },
    totalPages() {
      return Math.ceil(this.totalResults / this.pagination.rowsPerPage)
    },
    paginationPages() {
      if (this.totalPages > this.pagination.maxPagesShown) {
        return this.pagination.maxPagesShown
      } else {
        return this.totalPages
      }
    }
  },
  watch: {
    pageSize() {
      this.pagination.rowsPerPage = this.pageSize
    },
    caseTypeId() {
      this.performSearch()
    },
    searchTerm() {
      this.performSearch()
    }
  },
  created() {
    this.dataProvider.store = Vue.store
    this.performSearch()
  },
  methods: {
    async performSearch() {
      this.isSearching = true
      this.currentTab = 0
      this.searchError = ""
      const result = await this.dataProvider.getCaseTypeSearch(this.searchTerm, parseInt(this.caseTypeId), this.pagination.page - 1, this.pageSize)
      if (!result || result.error) {
        this.searchError = result && result.message ? result.message : "Search did not return a valid response."
        this.result = { cases: [], total: 0 }
        this.documentSearch = { documents: [], total: 0 }
        if (this.$store && this.$store.commit) {
          this.$store.commit("displayFlashMessage", { message: this.searchError, severity: "error" })
        }
        this.isSearching = false
        return
      }
      const caseType = await this.helpers.getCaseTypeById(this.$store.state, this.caseTypeId)
      this.caseType = caseType
      this.caseTypeName = this.helpers.caseTypeName(caseType)
      result.page = this.pagination.page
      this.result = result
      if (result && result.documents) {
        this.documentSearch = result.documents
      } else {
        this.documentSearch = { documents: [], total: 0 }
      }
      var documentTotal = 0
      if (this.documentSearch && this.documentSearch.total) {
        documentTotal = this.documentSearch.total
      }
      logFeature("document-search", "gui-results", "Case-type search returned document hits", {
        documents: documentTotal,
      })
      this.isSearching = false
    },
    async reloadSearch(pagination) {
      this.prevPage = this.pagination.page
      this.pagination = pagination
      this.performSearch()
    },
    setSelections(newSelections) {
      this.selections = newSelections
    },
    toggleSelectMode() {
      this.selectMode = !this.selectMode
      this.selections = []
    },
    openDeleteDialog(allMatching) {
      this.deleteAllMatching = allMatching
      if (this.deleteTargetCount < 1) return
      this.deleteDialog = true
    },
    async confirmDelete() {
      this.deleting = true
      let result
      if (this.deleteAllMatching) {
        result = await this.$store.dispatch("deleteSearchCases", {
          caseTypeId: parseInt(this.caseTypeId, 10),
          searchTerm: this.searchTerm,
          confirmedCount: this.caseTotal
        })
      } else {
        result = await this.$store.dispatch("deleteCases", this.selections)
      }
      this.deleting = false
      this.deleteDialog = false
      if (result) {
        this.selections = []
        this.selectMode = false
        this.performSearch()
      }
    },
    hasValidLink(header, data) {
      if (data === undefined || data === null) {
        return false
      }
      if (header.hasLink) {
        if (data.route !== "" && data.route !== undefined) { return true }
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.search-error-heading {
  color: #b71c1c;
}

.search-error {
  padding: 20px;
  color: #b71c1c;
  background: #fdecea;
}

.search-count-badge {
  display: inline-block;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  margin-left: 8px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  font-weight: 500;
}

.search-result-tab + .search-result-tab {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.search-results-tabs {
  max-width: 100%;
}

.search-results-tabs >>> .v-tabs__items,
.search-results-tabs >>> .v-tabs__content {
  max-width: 100%;
  overflow: hidden;
}

.search-result-pane {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}
</style>
